import React, { ReactElement, useState } from 'react';
import { useForm } from 'react-hook-form';
import { xIcon } from '@progress/kendo-svg-icons';
import cn from 'clsx';
import styles from './email-subject-actions-dialog.module.scss';
import Button from '@components-clean/core/button';
import Card from '@components-clean/core/card';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import { InfoField } from '@components-clean/core/info-field';
import { MultiSelectEmailController, Person } from '@components-clean/core/multi-select-email/multi-select-email';
import { TextAreaController } from '@components-clean/core/text-area';
import { TextBox } from '@components-clean/core/text-box';
import {
    SubjectActionsEmailPreviewDto,
    StudyTeamEmailMemberDto,
} from '@interfaces-clean/subject-monitoring/subject-actions-email.interface';
import { useTranslation } from '@hooks/index';
import { stringDateWithoutTimeFromUtc } from '@utils/convertor';
import { ShowSuccess, ShowError } from '@utils/notification';
import SubjectActionsApi from '@apis/bff-api/subject-monitoring/subject-actions';

export interface Props {
    onClose: () => void;
    previewData: SubjectActionsEmailPreviewDto | null;
    studyRef: string;
    studyNumber: string;
}

interface PreviewProps {
    data: SubjectActionsEmailPreviewDto;
    studyRef: string;
    studyNumber: string;
    onClose: () => void;
}

const LabelText = ({ children, inline }: React.PropsWithChildren & { inline?: boolean }): ReactElement => {
    return <span className={cn('k-font-weight-medium', { 'k-pr-2': inline })}>{children}</span>;
};

const toPerson = (member: StudyTeamEmailMemberDto): Person => ({
    email: member.email,
    firstName: member.name,
    lastName: '',
});

const EmailActionsPreview = ({ data, studyRef, studyNumber, onClose }: PreviewProps) => {
    const { trigger, isMutating: sendingEmail } = SubjectActionsApi.useSendEmail(studyRef);
    const { t } = useTranslation(['sm', 'common']);

    const [studyMembers] = useState<Person[]>(() => data.studyTeamMembers.map(toPerson));

    const {
        control,
        formState: { isValid },
        getValues,
        handleSubmit,
    } = useForm<{ cc: string; notes: string }>({
        mode: 'all',
        defaultValues: {
            cc: '',
            notes: '',
        },
    });

    const onSubmit = async () => {
        const requestValues = {
            to: data.ownerEmail,
            ccEmails: getValues('cc'),
            actions: data.actions.flatMap((a) => (a.id ? [a.id] : [])),
            timestamp: data.timestamp,
            notes: getValues('notes') || null,
        };
        try {
            const response = await trigger(requestValues);
            if (response) ShowSuccess(t('sm:monitoringToolkit.emailActionsDialog.sendSuccess'));
            else ShowError(t('common:errorMessage'));
        } catch {
            ShowError(t('common:errorMessage'));
        } finally {
            onClose();
        }
    };

    const sortedObservations = [...data.observations].sort((a, b) => {
        const dateA = a.created ? new Date(a.created).getTime() : 0;
        const dateB = b.created ? new Date(b.created).getTime() : 0;
        return dateB - dateA;
    });

    const body = (
        <div className="k-h-full k-w-full">
            <div className="k-w-full k-d-flex-col k-justify-content-start k-align-items-start">
                <Card
                    data-testid="sm-email-actions-details"
                    title={t('sm:monitoringToolkit.emailActionsDialog.emailSubject', { studyName: studyNumber })}
                    className="k-w-full"
                >
                    <div className="k-w-full" style={{ height: 327 }}>
                        <form id="sm-email-actions">
                            <div className="k-mb-2">
                                <TextBox
                                    label={t('sm:monitoringToolkit.emailActionsDialog.to')}
                                    data-testid="sm-email-actions-owner-email"
                                    readOnly
                                    value={data.ownerEmail || undefined}
                                />
                            </div>
                            <div className="k-mb-2">
                                <MultiSelectEmailController
                                    data-testid="sm-email-actions-cc-emails"
                                    label={t('sm:monitoringToolkit.emailActionsDialog.cc')}
                                    control={control}
                                    name="cc"
                                    data={studyMembers}
                                    placeholder={t('sm:monitoringToolkit.emailActionsDialog.ccHint')}
                                />
                            </div>
                            <div>
                                <TextAreaController
                                    className={styles.notesTextArea}
                                    data-testid="sm-email-actions-notes"
                                    maxLength={4000}
                                    label={t('sm:monitoringToolkit.emailActionsDialog.notes')}
                                    control={control}
                                    name="notes"
                                    placeholder={t('sm:monitoringToolkit.emailActionsDialog.notesHint')}
                                    validationTooltipPosition="auto"
                                />
                            </div>
                        </form>
                    </div>
                </Card>
                {sortedObservations.map((observation, i, row) => {
                    const obsActions = data.actions
                        .filter((a) => a.observationId === observation.id)
                        .sort((a, b) => (a.created ?? '').localeCompare(b.created ?? '') * -1);
                    const isLastItem = i + 1 === row.length;
                    const isFirstItem = i === 0;
                    return (
                        <React.Fragment key={observation.id}>
                            <Card
                                data-testid={`sm-email-actions-observation-${observation.id}`}
                                title={`${t('sm:monitoringToolkit.emailActionsDialog.observationId')} - ${observation.recordId}`}
                                className={cn(isFirstItem ? 'k-mt-6' : 'k-mt-3.5', styles.cardCustomBorder)}
                                bodyStyles={{ padding: 0 }}
                            >
                                <div className="k-d-flex-col">
                                    <div
                                        className={cn(
                                            styles.infoBlock,
                                            'k-d-flex-row k-justify-content-between k-align-items-center k-font-size-lg k-py-3 k-px-7.5',
                                        )}
                                    >
                                        <span>
                                            <LabelText inline>
                                                {t('sm:monitoringToolkit.emailActionsDialog.subject')}
                                            </LabelText>
                                            {observation.subject ?? 'N/A'}
                                        </span>
                                        <span>
                                            <LabelText inline>
                                                {t('sm:monitoringToolkit.emailActionsDialog.datasetOpenOn')}
                                            </LabelText>
                                            {observation.datasetOpenOn ?? 'N/A'}
                                        </span>
                                        <span>
                                            <LabelText inline>
                                                {t('sm:monitoringToolkit.emailActionsDialog.observationDate')}
                                            </LabelText>
                                            {observation.observationDate ?? 'N/A'}
                                        </span>
                                        <span>
                                            <LabelText inline>
                                                {t('sm:monitoringToolkit.emailActionsDialog.closedDate')}
                                            </LabelText>
                                            {observation.closedDate ?? 'N/A'}
                                        </span>
                                    </div>
                                    <div className="k-py-3 k-px-7.5 k-font-size-md">
                                        <div>
                                            <p>
                                                <span className="k-font-weight-medium k-pr-2 k-font-size-lg">
                                                    {observation.dataListing && !observation.panel
                                                        ? t('sm:monitoringToolkit.emailActionsDialog.dataListing')
                                                        : t('sm:monitoringToolkit.emailActionsDialog.panel')}
                                                </span>
                                                <span>{observation.panel ?? observation.dataListing ?? 'N/A'}</span>
                                            </p>
                                            <div>
                                                <p className="k-font-weight-medium k-font-size-lg">
                                                    {t('sm:monitoringToolkit.emailActionsDialog.observation')}
                                                </p>
                                                <p>{observation.observation ?? 'N/A'}</p>
                                            </div>
                                            <div>
                                                <p className="k-font-weight-medium k-font-size-lg">
                                                    {t('sm:monitoringToolkit.emailActionsDialog.rootCause')}
                                                </p>
                                                <p>{observation.rootCause || 'N/A'}</p>
                                            </div>
                                            <div>
                                                <p className="k-font-weight-medium k-font-size-lg">
                                                    {t('sm:monitoringToolkit.emailActionsDialog.comments')}
                                                </p>
                                                <p>{observation.comments || 'N/A'}</p>
                                            </div>
                                            <div className="k-font-size-lg">
                                                <span className="k-font-weight-medium k-pr-2">
                                                    {t('sm:monitoringToolkit.emailActionsDialog.created')}
                                                </span>
                                                <span>
                                                    {stringDateWithoutTimeFromUtc(observation.created, 'DD-MMM-YYYY') ??
                                                        'N/A'}
                                                </span>{' '}
                                                <span className="k-font-weight-medium k-pr-2">
                                                    {t('sm:monitoringToolkit.emailActionsDialog.by')}
                                                </span>
                                                <span>{observation.createdBy ?? 'N/A'}</span>
                                            </div>
                                            {obsActions.map((action) => (
                                                <Card
                                                    key={action.id}
                                                    data-testid={`sm-email-actions-action-${action.id}`}
                                                    className="k-mt-6"
                                                    title={`${t('sm:monitoringToolkit.emailActionsDialog.actionId')} - ${action.recordId}`}
                                                    padding="none"
                                                >
                                                    <div className="k-d-flex-col k-font-size-lg">
                                                        <div
                                                            className={cn(
                                                                styles.infoBlock,
                                                                'k-d-flex-row k-justify-content-start k-py-3 k-px-7.5',
                                                            )}
                                                        >
                                                            <span style={{ minWidth: 276 }}>
                                                                <LabelText inline>
                                                                    {t(
                                                                        'sm:monitoringToolkit.emailActionsDialog.requestedDate',
                                                                    )}
                                                                </LabelText>
                                                                {action.requestedDate ?? 'N/A'}
                                                            </span>
                                                            <span>
                                                                <LabelText inline>
                                                                    {t('sm:monitoringToolkit.emailActionsDialog.owner')}
                                                                </LabelText>
                                                                {action.owner ?? 'N/A'}
                                                            </span>
                                                        </div>
                                                        <div className="k-py-3 k-px-7.5">
                                                            <div>
                                                                <span className="k-font-weight-medium">
                                                                    {t(
                                                                        'sm:monitoringToolkit.emailActionsDialog.description',
                                                                    )}
                                                                </span>
                                                                <p>{action.description ?? 'N/A'}</p>
                                                            </div>
                                                            <div>
                                                                <span className="k-font-weight-medium">
                                                                    {t(
                                                                        'sm:monitoringToolkit.emailActionsDialog.comments',
                                                                    )}
                                                                </span>
                                                                <p>{action.comments ?? 'N/A'}</p>
                                                            </div>
                                                            <div>
                                                                <span className="k-font-weight-medium">
                                                                    {t(
                                                                        'sm:monitoringToolkit.emailActionsDialog.resolution',
                                                                    )}
                                                                </span>
                                                                <p>{action.resolution ?? 'N/A'}</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </Card>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </Card>
                            {!isLastItem && <hr className={styles.hrCustom} />}
                        </React.Fragment>
                    );
                })}
            </div>
        </div>
    );

    return (
        <DraggableDialog
            dataTestId="sm-email-actions-dialog"
            draggable={false}
            title={t('sm:monitoringToolkit.emailActionsDialog.title')}
            onSubmit={undefined}
            primaryBtnLabel={undefined}
            stage="FULLSCREEN"
            width={473}
            hideTitleCloseButton
            titleBarStyle="alternate"
            restoreButton={() => (
                <div className="k-d-flex k-flex-row k-gap-1">
                    <Button data-testid="sm-email-actions-cancel" onClick={onClose} primary disabled={sendingEmail}>
                        {t('common:cancel')}
                    </Button>
                    <Button
                        fillMode="solid"
                        data-testid="sm-email-actions-send"
                        onClick={() => handleSubmit(onSubmit)()}
                        disabled={!isValid || sendingEmail}
                        type="submit"
                    >
                        {t('sm:monitoringToolkit.emailActionsDialog.send')}
                    </Button>
                </div>
            )}
            setHeight="fit-content"
        >
            {body}
        </DraggableDialog>
    );
};

const EmailSubjectActionsDialog = ({ onClose, previewData, studyRef, studyNumber }: Props) => {
    const { t } = useTranslation('sm');

    return !previewData ? (
        <DraggableDialog
            dataTestId="sm-email-actions-warning-dialog"
            draggable={false}
            title={t('sm:monitoringToolkit.emailActionsDialog.warningTitle')}
            onSubmit={onClose}
            primaryBtnLabel={t('common:close')}
            height={145}
            stage="DEFAULT"
            width={478}
            minimizeButton={() => null}
            style={{}}
            closeButton={() => (
                <Button
                    data-testid="sm-email-actions-close-button"
                    onClick={onClose}
                    type="button"
                    fillMode="flat"
                    svgIcon={xIcon}
                />
            )}
        >
            <div className="k-d-flex-col">
                <InfoField message={t('sm:monitoringToolkit.emailActionsDialog.warningBody')} type="Warning" />
            </div>
        </DraggableDialog>
    ) : (
        <EmailActionsPreview data={previewData} studyRef={studyRef} studyNumber={studyNumber} onClose={onClose} />
    );
};

export default EmailSubjectActionsDialog;
